import { HTMLAttributes } from "react";
import styled from "styled-components";
import checkSignWhite from "../../assets/new-checkmark-white.svg";
import checkSignDisabled from "../../assets/new-checkmark-disabled.svg";

interface Props extends HTMLAttributes<HTMLInputElement> {
  checked: boolean;
  disabled?: boolean;
  id?: string;
  label: string | JSX.Element;
  name: string;
  required?: boolean;
}

const CheckboxField = ({
  checked,
  className,
  disabled = false,
  id,
  label,
  name,
  onChange,
  required,
}: Props) => {
  return (
    <FieldContainer className={className}>
      <MainContainer>
        <Input
          checked={checked}
          disabled={disabled}
          id={id || name}
          name={name}
          type="checkbox"
          onChange={onChange}
          required={required}
        />
        <LabelContainer>
          <Label disabled={disabled} htmlFor={id || name}>
            {label}
          </Label>
        </LabelContainer>
      </MainContainer>
    </FieldContainer>
  );
};

const FieldContainer = styled.div`
  color: ${({ theme }) => theme.defaultColor};
  font-family: ${({ theme }) => theme.regularFont};
  display: flex;
  flex-direction: column;
  min-height: 20px;
`;

const MainContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Input = styled.input`
  background: ${({ theme }) => theme.defaultBackground};
  border: 1px solid ${({ theme }) => theme.defaultColor};
  -webkit-appearance: none;
  align-items: center;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  width: 20px;
  border-radius: 0px;
  margin: 0;

  &:checked {
    background: ${({ theme }) => theme.form.checkbox.checkedBackground};

    &::after {
      background-image: url(${checkSignWhite});
      background-repeat: no-repeat;
      content: "";
      height: 9px;
      line-height: 100%;
      width: 12px;
    }
  }

  &:disabled {
    background: ${({ theme }) => theme.defaultBackground};
    border: 1px solid ${({ theme }) => theme.disabledColor};
    cursor: unset;

    &:checked::after {
      background-image: url(${checkSignDisabled});
    }
  }
`;

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  flex-direction: row;
  align-items: center;

  & > * {
    align-items: center;
    display: flex;
  }
`;

const Label = styled.label<{ disabled: boolean }>`
  color: ${({ disabled, theme }) => (disabled ? theme.disabledColor : theme.defaultColor)};
  cursor: ${({ disabled }) => (disabled ? "" : "pointer")};
  display: block;
  font-size: 14px;
  line-height: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
`;

export default CheckboxField;
